// Helpers for turning machine names (snake_case columns, PascalCase model
// classes) into reader-friendly labels for the audit log UI.
//
// Lookup order for both helpers:
//   1. i18n key (`history.fields.<name>` / `history.models.<name>`).
//   2. Mechanical fallback (snake_case → Title Case).
//
// Add specific i18n entries to `app/frontend/locales/{en,es}.json` whenever
// the auto-humanized version reads poorly in either language.

import type { TFunction } from "i18next"

function titleize(token: string): string {
    if (token.length === 0) return token
    if (token.toLowerCase() === "id") return "ID"
    return token.charAt(0).toUpperCase() + token.slice(1)
}

export function humanizeSnake(value: string): string {
    return value
        .split("_")
        .filter(Boolean)
        .map(titleize)
        .join(" ")
}

export function humanizePascal(value: string): string {
    return value
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
}

export function humanizeField(t: TFunction, model: string | undefined, field: string): string {
    // 1. Per-model override: `history.fields.<Model>.<field>`.
    if (model) {
        const specific = t(`history.fields.${model}.${field}`, { defaultValue: "" })
        if (specific) return specific
    }
    // 2. Generic override: `history.fields.<field>` (when meaning is the same
    //    across every model that has this column).
    const generic = t(`history.fields.${field}`, { defaultValue: "" })
    if (generic) return generic
    // 3. Mechanical fallback (English-flavored Title Case).
    return humanizeSnake(field)
}

export function humanizeModel(t: TFunction, model: string): string {
    return t(`history.models.${model}`, { defaultValue: humanizePascal(model) })
}
